import { useTranslation } from "react-i18next";
import Card from "components/card/Card";
import Page from "components/page/Page";
import Input from "components/form/input/Input";
import React, { useEffect, useState } from "react";
import { isEmpty } from "helpers/Helpers";
import FormLayout from "components/form/FormLayout";
import { GetPaymentSettings, PutPaymentSettings } from "api/rpc/2024-04/facilityAdmin/facility/paymentSettings";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import { cloneDeep, isEqual } from "lodash";

interface IPaymentGeneralState {
  paymentSettingsId: number;
  paymentSettingsLoaded: boolean;
}

interface IPaymentSettings {
  refundLimitDaily: string;
  refundLimitWeekly: string;
}

export default function PaymentGeneral() {
  const { authStore } = useAppSelector(store => store);
  const { t } = useTranslation();

  const permissions = authStore?.user?.permissions;

  const dispatch = useAppDispatch();

  const [paymentGeneralState, setPaymentGeneralState] = useState<IPaymentGeneralState>({
    paymentSettingsId: null,
    paymentSettingsLoaded: false,
  });

  const [paymentSettings, setPaymentSettings] = useState<IPaymentSettings>(undefined);
  const [paymentSettingsBeforeChanges, setPaymentSettingsBeforeChanges] = useState<IPaymentSettings>(undefined);

  useEffect(() => {
    void loadPaymentSettings();
  }, []);

  async function loadPaymentSettings() {
    const getPaymentSettingsRes = await GetPaymentSettings(null, true);

    if (getPaymentSettingsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Failed to load payment settings")));
      return;
    }

    const paymentSettings: IPaymentSettings = {
      refundLimitDaily: String(getPaymentSettingsRes.data.refund_limit_daily),
      refundLimitWeekly: String(getPaymentSettingsRes.data.refund_limit_weekly),
    };

    setPaymentSettings(paymentSettings);
    setPaymentSettingsBeforeChanges(paymentSettings);

    setPaymentGeneralState(prev => ({
      ...prev,
      paymentSettingsId: getPaymentSettingsRes.data.id,
      paymentSettingsLoaded: true,
    }));
  }

  function unsavedChangesExist() {
    return !isEqual(paymentSettings, paymentSettingsBeforeChanges);
  }

  function limitIsValid(limit: string) {
    const numericLimit = Number(limit);

    return !isEmpty(limit) && !isNaN(numericLimit) && numericLimit >= 0;
  }

  async function savePaymentSettings() {
    if (!limitIsValid(paymentSettings.refundLimitDaily) || !limitIsValid(paymentSettings.refundLimitWeekly)) {
      return;
    }

    const putPaymentSettingsRes = await PutPaymentSettings(
      {
        id: paymentGeneralState.paymentSettingsId,
        refund_limit_daily: Number(paymentSettings.refundLimitDaily),
        refund_limit_weekly: Number(paymentSettings.refundLimitWeekly),
      },
      true,
    );

    if (putPaymentSettingsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Failed to save payment settings")));
      return;
    }

    setPaymentSettingsBeforeChanges(cloneDeep(paymentSettings));
  }

  function cancelUnsavedChanges() {
    setPaymentSettings(cloneDeep(paymentSettingsBeforeChanges));
  }

  return (
    <>
      {paymentGeneralState.paymentSettingsLoaded && (
        <Page
          title={t("General")} // TODO: Translation
          notificationBarProps={{
            isVisible: unsavedChangesExist(),
            onAction: savePaymentSettings,
            onCancel: cancelUnsavedChanges,
          }}
          narrow
        >
          <Card>
            <Card.Section title="Refund Limits">
              {/* TODO: Translation */}
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    label={t("Daily Limit")}
                    prefix="$"
                    value={paymentSettings.refundLimitDaily}
                    error={!limitIsValid(paymentSettings.refundLimitDaily)}
                    onChange={e => setPaymentSettings(prev => ({ ...prev, refundLimitDaily: e.target.value }))}
                    readOnly={!permissions?.payment_settings_edit}
                  />
                  <Input
                    label={t("Weekly Limit")}
                    prefix="$"
                    value={paymentSettings.refundLimitWeekly}
                    error={!limitIsValid(paymentSettings.refundLimitWeekly)}
                    onChange={e => setPaymentSettings(prev => ({ ...prev, refundLimitWeekly: e.target.value }))}
                    readOnly={!permissions?.payment_settings_edit}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>
        </Page>
      )}
    </>
  );
}
